import { log } from "./log";
import open from "open";
import { verbose } from "@/cli/dev";
import type { ApiSubscriptionResponse } from "@/pages/api/cli/subscription";
import { getBaseUrl } from "@/config";

async function waitForSubscription(token: string): Promise<boolean> {
  let attempts = 0;
  const maxAttempts = 3 * 60; // 3 minutes timeout

  while (attempts < maxAttempts) {
    verbose && console.log("Waiting for subscription...", attempts);

    // Check subscription status
    const response = await fetch(
      `${getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT })}/api/cli/subscription`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      verbose &&
        console.log(
          `Subscription check failed: ${response.status} ${response.statusText}`,
        );
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait before retrying
      attempts++;
      continue;
    }

    const data = await response.json();

    if (data.hasActiveSubscription) {
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, 1000)); // Poll every 1 second
    attempts++;
  }

  return false;
}

export async function ensureHasSubscription(
  email: string,
  token: string,
): Promise<boolean> {
  // Check for existing active subscription
  const response = await fetch(
    `${getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT })}/api/cli/subscription`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    verbose &&
      console.log(
        `Subscription check failed: ${response.status} ${response.statusText}`,
      );
    // If we can't check the subscription status, assume we need to create one
    log.warning(
      "Could not verify subscription status. Proceeding with checkout.",
    );
  } else {
    const data = (await response.json()) as ApiSubscriptionResponse;

    if (data.ok && data.hasActiveSubscription) {
      verbose && console.log("Active subscription found");
      return true;
    } else {
      verbose && console.log("No active subscription found");
    }
  }

  log.title("\n💳 Payment Required");
  log.info("\nStarting Stripe checkout...");

  try {
    // Create checkout session through API
    const checkoutResponse = await fetch(
      `${getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT })}/api/stripe/session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      },
    );

    if (!checkoutResponse.ok) {
      console.log(
        "baseUrl",
        getBaseUrl({ prodUrl: !process.env.BEEPS_DEVELOPMENT }),
      );
      console.log(await checkoutResponse.text());
      throw new Error("Failed to create checkout session");
    }

    const { url } = await checkoutResponse.json();
    if (!url) {
      throw new Error("No checkout URL received");
    }

    // Open checkout in browser
    log.info("\nOpening checkout page...");
    log.info(`\nCheckout page URL\n${url}`);
    await open(url);

    // Wait for subscription
    verbose && log.info("\nWaiting for subscription completion...");
    const success = await waitForSubscription(token);

    if (!success) {
      log.error("\nSubscription timeout. Please try again.");
      return false;
    }

    log.success("\n✨ Subscription activated!");
    return true;
  } catch (error) {
    if (error instanceof Error) {
      log.error(`\nCheckout failed: ${error.message}`);
    } else {
      log.error("\nCheckout failed: Unknown error");
    }
    return false;
  }
}
